import { GatewayQuestionnaire } from "@conformite/gateway";
import DOMPurify from "dompurify";
import {
  BannerCard,
  LoadingPage,
} from "@endya-conformite/ui-kit/src/components";
import {
  FormProvider,
  useForm,
} from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import { FormGrid, formatDateHour } from "@endya-conformite/ui-kit";
import { useQuestionnaireInstancesQuery } from "@src/data/questionnaire/hook/useQuestionnaireInstancesQuery";
import { useState } from "react";
import { QuestionnaireQuestion } from "../QuestionnaireQuestion/QuestionnaireQuestion";
import styles from "./QuestionnaireHistory.module.scss";

type QuestionnaireHistoryProps = {
  contextType: string;
  contextId: string;
  titlePrefix?: string;
};

function HistoryInstanceDisplay({
  instance,
}: {
  instance: GatewayQuestionnaire.QuestionnaireInstanceResponse;
}) {
  const defaultValues: Record<string, string> = Object.fromEntries(
    instance.answers.map((a) => [a.questionDefinitionId, a.value])
  );

  const formMethods = useForm<Record<string, string>>({ defaultValues });

  return (
    <FormProvider {...formMethods}>
      <div className={styles.historyContent}>
        <FormGrid disableHorizontalGutter>
          {instance.answers.map((answer) => {
            const virtualQuestion: GatewayQuestionnaire.QuestionDefinitionResponse =
              {
                id: answer.questionDefinitionId,
                label: answer.label,
                options: answer.options,
                helpText: answer.helpText ?? null,
                indicationIA: null,
                order: 0,
                required: false,
                condition: null,
                type: answer.value.startsWith("[") ? "multiple" : "single",
              };
            const aiAnswer = answer.aiAnswer
              ? {
                  answer: answer.aiAnswer.answer,
                  justification: answer.aiAnswer.justification ?? "",
                  evidence: answer.aiAnswer.evidence,
                  confidence: answer.aiAnswer.confidence,
                  guardrail: answer.aiAnswer.guardrail,
                  evidenceGrounded: answer.aiAnswer.evidenceGrounded,
                }
              : undefined;
            return (
              <QuestionnaireQuestion
                key={answer.questionDefinitionId}
                question={virtualQuestion}
                aiAnswer={aiAnswer}
                disabled
              />
            );
          })}
        </FormGrid>

        {instance.comment && (
          <div className={styles.commentBlock}>
            <span className={styles.commentLabel}>Commentaire</span>
            {/* eslint-disable-next-line react/no-danger */}
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(instance.comment),
              }}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
}

function HistoryNav({
  selectedIndex,
  total,
  onPrev,
  onNext,
}: {
  selectedIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className={styles.bannerNav}>
      <button
        type="button"
        className={styles.bannerNavBtn}
        onClick={onNext}
        disabled={selectedIndex === 0}
        aria-label="Contrôle plus récent"
      >
        &#8249;
      </button>
      <span className={styles.bannerNavCounter}>
        {selectedIndex + 1}/{total}
      </span>
      <button
        type="button"
        className={styles.bannerNavBtn}
        onClick={onPrev}
        disabled={selectedIndex >= total - 1}
        aria-label="Contrôle plus ancien"
      >
        &#8250;
      </button>
    </div>
  );
}

export function QuestionnaireHistory({
  contextType,
  contextId,
  titlePrefix = "Contrôle quinquennal",
}: QuestionnaireHistoryProps) {
  const { data: instances, isLoading } = useQuestionnaireInstancesQuery(
    contextType,
    contextId
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (isLoading) return <LoadingPage withText={false} />;

  if (!instances || instances.length === 0) return null;

  const sorted = [...instances].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const total = sorted.length;
  const current = sorted[selectedIndex];

  return (
    <div className={styles.historyCard}>
      <BannerCard
        title={`${titlePrefix} du ${formatDateHour(new Date(current.createdAt))}`}
        bannerRight={
          total > 1 ? (
            <HistoryNav
              selectedIndex={selectedIndex}
              total={total}
              onPrev={() => setSelectedIndex((i) => i + 1)}
              onNext={() => setSelectedIndex((i) => i - 1)}
            />
          ) : undefined
        }
      >
        <HistoryInstanceDisplay key={current.id} instance={current} />
      </BannerCard>
    </div>
  );
}
